import { computed, inject } from '@angular/core';
import {
  signalStore,
  withComputed,
  withMethods,
  withState,
  patchState,
} from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';
import { encodeApiError, TApiErrorResponse } from '@sandbox-platform/shared-ui';
import { AttendanceSettingsService } from '../../attendance-settings.service';
import { AttendanceAutoCloseLogDTO } from '../../attendance-settings.types';

interface AttendanceAutoCloseLogState {
  logs: AttendanceAutoCloseLogDTO[];
  loading: boolean;
  error: string | TApiErrorResponse | null;
}

const initialState: AttendanceAutoCloseLogState = {
  logs: [],
  loading: false,
  error: null,
};

export const AttendanceAutoCloseLogStore = signalStore(
  { providedIn: 'root' },

  withState(initialState),

  withComputed((store) => ({
    hasLogs: computed(() => store.logs().length > 0),
    isLoading: computed(() => store.loading()),
  })),

  withMethods((store, apiService = inject(AttendanceSettingsService)) => ({
    async loadLogs(days = 15): Promise<void> {
      patchState(store, { loading: true, error: null });

      try {
        const response = await firstValueFrom(apiService.getLogs(days));
        patchState(store, { logs: response?.data ?? [] });
      } catch (error) {
        patchState(store, {
          error: encodeApiError(error, 'Failed to load auto-close logs'),
        });
      } finally {
        patchState(store, { loading: false });
      }
    },

    clearError(): void {
      patchState(store, { error: null });
    },
  }))
);

export type AttendanceAutoCloseLogStoreType = InstanceType<
  typeof AttendanceAutoCloseLogStore
>;
